import SubmitContext from 'context/submit/submitContext'
import defaultImage from 'assets/img/image_placeholder_2.png'
import loadingImage from 'assets/img/Preloader_8.gif'
import PropTypes from 'prop-types'
import React, { useRef, useState, useEffect, useContext } from 'react'
import { Button, Modal, ModalHeader, ModalBody, Row } from 'reactstrap'
import ReactImageZoom from 'react-image-zoom'
import { pdfjs } from 'react-pdf'
import { useFormContext } from 'react-hook-form'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

const MAX_IMAGE_FILE_SIZE = 4194304
const MAX_BASE64_IMAGE_LENGTH = Math.floor(MAX_IMAGE_FILE_SIZE / 3) * 4

const getBase64Payload = imageData =>
  imageData?.substring(imageData.indexOf(',') + 1) || ''

const isEncodedImageWithinLimit = imageData =>
  getBase64Payload(imageData).length <= MAX_BASE64_IMAGE_LENGTH

const getPage = (pdf, num) => {
  return new Promise((resolve, reject) => {
    pdf.getPage(num).then(page => {
      const scale = '2'
      const viewport = page.getViewport({
        scale: scale,
      })
      const canvas = document.createElement('canvas')
      const canvasContext = canvas.getContext('2d')
      canvas.height =
        viewport.height || viewport.viewBox[3] /* viewport.height is NaN */
      canvas.width =
        viewport.width || viewport.viewBox[2] /* viewport.width is also NaN */
      page
        .render({
          canvasContext,
          viewport,
        })
        .promise.then(res => {
          resolve(canvas.toDataURL('image/jpeg', 1))
        })
    })
  })
}

// unfinished implementation for multiple page PDF preview
// const getAllPages = (pdf, num) => {
//   return new Promise((resolve, reject) => {
//     const canvasContainer = document.createElement('canvas')
//     let ctx = canvasContainer.getContext('2d')
//     for (var num = 1; num <= pdf.numPages; num++) {
//       pdf.getPage(num).then(page => {
//         const scale = '2'
//         const viewport = page.getViewport({
//           scale: scale,
//         })
//         const canvas = document.createElement('canvas')
//         const canvasContext = canvas.getContext('2d')
//         canvas.height =
//           viewport.height || viewport.viewBox[3] /* viewport.height is NaN */
//         canvas.width =
//           viewport.width || viewport.viewBox[2] /* viewport.width is also NaN */
//         ctx.drawImage(canvas, 0, 0)
//         page.render({
//           canvasContext,
//           viewport,
//         })
//       })
//     }
//     console.log('final data is: ' + canvasContainer.toDataURL('image/jpeg, 1'))
//     resolve(canvasContainer.toDataURL('image/jpeg, 1'))
//   })
// }

const ImageUpload = ({
  index,
  imageInfo,
  updateImageInfo,
  isSubmitter,
  isDisabled,
}) => {
  const methods = useFormContext()
  const { clearError, setError } = methods

  const submitContext = useContext(SubmitContext)
  const { resetting } = submitContext

  const [isOpen, setIsOpen] = React.useState(false)
  const [imageFile, setImageFile] = useState({
    name: null,
  })
  const [imageIndex] = useState(index)
  const [imagePreviewUrl, setImagePreviewUrl] = useState(defaultImage)
  const fileInput = useRef()

  const imageDimensions = useRef()
  const [imageWidth, setImageWidth] = useState(0)

  const saveImageInfo = (imageData, fileName) => {
    setImagePreviewUrl(imageData)
    // Add data to current product
    updateImageInfo(imageIndex, {
      ImageName: fileName ?? null,
      ImageType: imageData.substring(0, imageData.indexOf(',') + 1),
        ProdImg: imageData.substring(
          imageData.indexOf(',') + 1,
          imageData.length
        ),
    })
  }

  const rejectOversizedImage = () => {
    updateImageInfo(imageIndex, {
      ImageName: '',
      ImageType: '',
      ProdImg: '',
    })
    setImagePreviewUrl(defaultImage)
    setImageFile({
      name: null,
    })
    if (fileInput.current) {
      fileInput.current.value = null
    }
    setError(
      'ImagesUpload',
      'tooLarge',
      '⚠ Image exceeds the 4 MB maximum size after encoding. Please use a smaller JPG or single page PDF.'
    )
  }

  const handleImageChange = e => {
    e.preventDefault()
    const reader = new FileReader()
    const file = e.target.files[0]
    reader.onloadend = () => {
      let imageData = reader.result
      setImageFile(file)
      console.log('imageFile is: ' + JSON.stringify(imageFile))
      if (file.type.includes('pdf')) {
        pdfjs.getDocument(imageData).promise.then(pdf => {
          getPage(pdf, 1).then(result => {
            // getAllPages(pdf, 1).then(result => {
            // the result is the base 64 version of image
            if (isEncodedImageWithinLimit(result)) {
              saveImageInfo(result, file.name)
              clearError('ImagesUpload')
            } else {
              rejectOversizedImage()
            }
          })
        })
      } else if (file.type.includes('jpeg')) {
        if (isEncodedImageWithinLimit(imageData)) {
          saveImageInfo(imageData, file.name)
          clearError('ImagesUpload')
        } else {
          rejectOversizedImage()
        }
      }
    }

    if (file?.type !== undefined) {
      if (file.type.includes('pdf')) {
        setImagePreviewUrl(loadingImage)
        reader.readAsArrayBuffer(file)
      } else if (file.type.includes('jpeg')) {
        setImagePreviewUrl(loadingImage)
        reader.readAsDataURL(file)
      } else {
        setError(
          'ImagesUpload',
          'wrongFormat',
          '⚠ Please use a JPG (preferred) or single page PDF.'
        )
      }
    }
  }

  useEffect(() => {
    if (imageInfo?.ImageName?.length > 0) {
      setImageFile({
        name: imageInfo.ImageName,
      })
    } else {
      setImageFile({
        name: null,
      })
    }

    if (imageInfo?.ProdImg?.length > 0) {
      let image = imageInfo.ImageType + imageInfo.ProdImg
      setImagePreviewUrl(image)
    } else {
      setImagePreviewUrl(defaultImage)
    }
  }, [imageInfo])

  const hideReview = () => setIsOpen(false)

  const handleReview = () => {
    setImageWidth(imageDimensions.current.naturalWidth)
    setIsOpen(true)
  }

  const handleClick = () => {
    clearError('ImagesUpload')
    fileInput.current.click()
  }

  const handleRemove = () => {
    // Remove data from current product
    updateImageInfo(imageIndex, {
      ImageName: '',
      ImageType: '',
      ProdImg: '',
    })
    fileInput.current.value = null
  }

  /* fileInput.current.value must be reset on submitter side or the same file won't load after submission
  fileInput.current.value must NOT be reset on the reviewer side, or images will not appear after first approval/denial */

  useEffect(() => {
    if (resetting && isSubmitter) {
      handleRemove()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetting])

  return (
    <div className="fileinput justify-content-center">
      <Modal isOpen={isOpen} toggle={hideReview} size={'lg'}>
        <ModalHeader toggle={hideReview}>Image: {imageFile?.name}</ModalHeader>
        <ModalBody>
          <h5 className="text-center">Hover mouse to magnify image.</h5>
          <Row className={'mt-lg-0 p-lg-0'}>
            <ReactImageZoom
              width={imageWidth > 800 ? 800 : imageWidth}
              zoomPosition="right"
              img={imagePreviewUrl}
            />
          </Row>
        </ModalBody>
      </Modal>
      <input
        type="file"
        accept=".jpg, .jpeg, .pdf"
        onChange={handleImageChange}
        ref={fileInput}
      />
      <img ref={imageDimensions} src={imagePreviewUrl} alt="Product" />
      <div>
        {imageFile.name === null ? (
          <Button
            className="btn-round btn-sm"
            disabled={isDisabled}
            onClick={handleClick}
          >
            Select image
          </Button>
        ) : (
          <div>
            <p className="text-center">Image: {imageFile?.name}</p>

            <Button
              color="danger"
              className="btn-round btn-sm"
              disabled={isDisabled}
              onClick={() => {
                handleRemove()
              }}
            >
              <i className="fa fa-times" />
              Remove
            </Button>
            <Button
              color="info"
              className="btn-round btn-sm"
              onClick={handleReview}
            >
              <i className="fa fa-search" />
              Review
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

ImageUpload.propTypes = {
  avatar: PropTypes.bool,
  imageInfo: PropTypes.object,
  Name: PropTypes.string,
  ProdImg: PropTypes.string,
  index: PropTypes.number,
  setNewInfo: PropTypes.func,
  updateImageInfo: PropTypes.func,
}

export default ImageUpload
